/**
 * Hook pour les données de référence des métiers (TradeReference)
 *
 * Récupère les métiers disponibles depuis la table TradeReference (DB).
 * Données système stables — staleTime de 30 minutes.
 *
 * Architecture: Component → Hook → tradesAPI → GET /api/core/trades
 */

import { useQuery } from '@tanstack/react-query';
import { tradesAPI } from '../utils/api';

export interface TradeReference {
  code: string;
  label: string;
  businessType: string;
  category: string;
  defaultUnits: string[];
  decennaleRequired: boolean;
  reverseChargeEligible: boolean;
  defaultVatRate: number;
  sortOrder: number;
  isActive: boolean;
}

export interface TradeCategory {
  category: string;
  trades: TradeReference[];
}

export const tradeReferenceKeys = {
  all: ['trade-references'] as const,
  list: () => [...tradeReferenceKeys.all, 'list'] as const,
  suggest: (apeCode: string) => [...tradeReferenceKeys.all, 'suggest', apeCode] as const,
};

/**
 * Hook useTradeReferences
 *
 * Retourne tous les métiers actifs groupés par catégorie.
 * Données système stables — 30min de staleTime.
 *
 * @example
 * const { categories, trades, isLoading } = useTradeReferences();
 * // categories = [{ category: 'Gros Oeuvre BTP', trades: [...] }, ...]
 * // trades = flat list of all trades
 */
export function useTradeReferences() {
  const { data, isLoading, error } = useQuery<TradeCategory[]>({
    queryKey: tradeReferenceKeys.list(),
    queryFn: () => tradesAPI.getAll(),
    staleTime: 30 * 60 * 1000, // 30 minutes — données système stables
    refetchOnWindowFocus: false,
  });

  const trades = data?.flatMap((cat) => cat.trades) ?? [];

  return {
    categories: data ?? [],
    trades,
    isLoading,
    error: error?.message ?? null,
  };
}

/**
 * Hook useTradeFromAPE
 *
 * Suggère un métier à partir d'un code APE.
 *
 * @example
 * const { suggestedTrade, isLoading } = useTradeFromAPE('4321A');
 * // suggestedTrade = { code: 'ELECTRICIEN', label: 'Électricien', ... }
 */
export function useTradeFromAPE(apeCode: string | null | undefined) {
  const { data, isLoading } = useQuery<TradeReference | null>({
    queryKey: tradeReferenceKeys.suggest(apeCode ?? ''),
    queryFn: () => tradesAPI.suggestFromAPE(apeCode!),
    enabled: !!apeCode,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    suggestedTrade: data ?? null,
    isLoading,
  };
}
